import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "../lib/reveal";
import { generateCapabilityPdf } from "../lib/deckPdf";
import { FileDown, Loader2, Lock } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gated one-page capability PDF: captures a lead, then downloads the deck.
export const CapabilityDeck = ({ context = "" }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", organization: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t("Please add your name and email.", "कृपया अपना नाम और ईमेल दर्ज करें।"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        organization: form.organization || undefined,
        segment: "Capability Deck",
        message: `Downloaded the capability deck${context ? ` (${context})` : ""}.`,
      });
      generateCapabilityPdf(context, form.name);
      setDone(true);
      toast.success(t("Your capability deck is downloading.", "आपका कैपेबिलिटी डेक डाउनलोड हो रहा है।"));
    } catch (err) {
      toast.error(t("Something went wrong. Please try again.", "कुछ गड़बड़ हो गई। कृपया पुनः प्रयास करें।"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent rounded-none border-0 border-b border-bone/30 py-2.5 text-base text-bone placeholder:text-bone/50 focus:outline-none focus:border-terracotta transition-colors duration-300";

  return (
    <Reveal>
      <div data-testid="capability-deck" className="grain relative overflow-hidden bg-forest text-bone rounded-3xl p-8 md:p-12">
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 text-terracotta mb-4">
              <FileDown size={18} />
              <span className="text-xs tracking-[0.16em] uppercase font-semibold">{t("Free download", "निःशुल्क डाउनलोड")}</span>
            </div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight">
              {t("Get the one-page OED capability deck.", "एक-पृष्ठ OED कैपेबिलिटी डेक प्राप्त करें।")}
            </h3>
            <p className="mt-4 text-bone/70 leading-relaxed max-w-md">
              {t(
                `A concise overview of results, applications and deployments — ideal to circulate internally with your team${context ? ` in ${context}` : ""}.`,
                `परिणामों, अनुप्रयोगों व तैनातियों का संक्षिप्त अवलोकन — ${context ? `${context} में ` : ""}अपनी टीम के साथ आंतरिक रूप से साझा करने हेतु आदर्श।`
              )}
            </p>
            <div className="flex items-center gap-2 text-bone/50 text-xs mt-6">
              <Lock size={13} /> {t("We'll only use your details to follow up on a pilot.", "हम आपके विवरण का उपयोग केवल पायलट हेतु संपर्क करने के लिए करेंगे।")}
            </div>
          </div>

          {done ? (
            <div data-testid="deck-success" className="text-center md:text-left">
              <p className="font-serif text-2xl text-bone">{t("Thank you — your download has started.", "धन्यवाद — आपका डाउनलोड शुरू हो गया है।")}</p>
              <button
                onClick={() => generateCapabilityPdf(context, form.name)}
                data-testid="deck-redownload"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-bone text-forest text-sm tracking-[0.08em] uppercase px-6 py-3 hover:bg-terracotta hover:text-bone transition-colors duration-300"
              >
                <FileDown size={16} /> {t("Download again", "फिर से डाउनलोड करें")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="deck-form" className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input data-testid="deck-name" value={form.name} onChange={set("name")} placeholder={t("Name *", "नाम *")} className={inputCls} />
                <input data-testid="deck-email" type="email" value={form.email} onChange={set("email")} placeholder={t("Work email *", "कार्य ईमेल *")} className={inputCls} />
              </div>
              <input data-testid="deck-org" value={form.organization} onChange={set("organization")} placeholder={t("Organization / municipality", "संगठन / नगरपालिका")} className={inputCls} />
              <button
                type="submit"
                data-testid="deck-submit"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full bg-bone text-forest text-sm tracking-[0.08em] uppercase px-7 py-4 hover:bg-terracotta hover:text-bone transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                {loading ? t("Preparing…", "तैयार हो रहा है…") : t("Download deck (PDF)", "डेक डाउनलोड करें (PDF)")}
              </button>
            </form>
          )}
        </div>
      </div>
    </Reveal>
  );
};
